import React, { useCallback, useEffect, useMemo, useRef } from "react";

import type {
  ColDef,
  ColGroupDef,
  GetRowIdParams,
  GridReadyEvent,
} from "ag-grid-community";
import { Grid } from "@material-ui/core";
import { Formik, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import {
  InterestPreviewAction,
  type InterestPreviewRequest,
} from "actions/interestPreview";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { InputDateRange } from "components/DateRangePicker/dateRangePicker";
import { Button, SecondaryButton } from "components/Form";
import {
  Autocomplete,
  OptionTogglerField,
} from "components/Form/FormikControls";
import { Column } from "components/Layout";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import {
  BillingDocumentColId,
  BillingDocumentColumnDef,
  BusinessColumnDef,
  InterestPreviewColumnDef,
} from "constants/columnDefinitions";
import { AgGridTables } from "constants/aggrid";
import { CreditDebit } from "constants/interest";
import { ModalTypes } from "constants/navigation";

import { useCanUseInterestPreview } from "hooks";

import { filterColIdsFromColumnDef } from "lib/agGrid";
import { formatISO8601DateString } from "lib/timeFormats";

import type { InterestPreviewAggridRow } from "selectors/aggrid/interestPreview";

import {
  getBusinesses,
  getContextByModalType,
  getIsCreatingInterestCharges,
  getProperties,
  getIsFetchingInterestPreview,
  selectInterestPreviewAgGridData,
} from "selectors";

interface InterestPreviewProps {
  onClose: () => void;
}

interface InterestPreviewModalContext {
  businessIds?: string[];
}

interface InterestPreviewFormValues {
  businessIds: string[];
  chargeFromDate: string | null;
  chargeToDate: string;
  creditDebit: NonNullable<InterestPreviewRequest["creditDebit"]>;
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: CreditDebit.DEBIT, label: "Invoice" },
  { value: CreditDebit.CREDIT, label: "RCTI" },
  { value: CreditDebit.BOTH, label: "Invoice and RCTI" },
];

const MAX_VISIBLE_BUSINESS_TAGS = 5;

const BUSINESSES_PLACEHOLDER = "All businesses";

const DATE_RANGE_TOOLTIP =
  "Interest is calculated over the selected date range, inclusive of the charge-to date.";

const getInterestPreviewRowId = (
  params: GetRowIdParams<InterestPreviewAggridRow>,
) => params.data.interestPreview.id;

const billingDocumentColumns = filterColIdsFromColumnDef(
  BillingDocumentColumnDef,
  [BillingDocumentColId.VENDOR_NUMBER, BillingDocumentColId.VENDOR_COMMISSION],
);

const columnDefs: (ColDef | ColGroupDef)[] = [
  {
    headerName: "Business",
    children: Object.values(BusinessColumnDef) as ColDef[],
  },
  {
    headerName: "Billing Document",
    children: billingDocumentColumns as ColDef[],
  },
  {
    headerName: "Interest Preview",
    children: Object.values(InterestPreviewColumnDef) as ColDef[],
  },
];

const validationSchema = Yup.object().shape({
  businessIds: Yup.array().of(Yup.string()),
  chargeFromDate: Yup.string()
    .nullable()
    .test(
      "valid-date-range",
      "Charge From must not be after Charge To",
      function isValidDateRange(chargeFromDate) {
        return (
          !chargeFromDate ||
          !this.parent.chargeToDate ||
          chargeFromDate <= this.parent.chargeToDate
        );
      },
    ),
  chargeToDate: Yup.string().required("Charge To is required"),
});

interface BusinessOption {
  value: string;
  label: string;
}

const getBusinessOptionValue = (option: BusinessOption) => option.value;

function InterestPreviewModal({ onClose }: InterestPreviewProps) {
  const dispatch = useDispatch();
  const canUseInterestPreview = useCanUseInterestPreview();
  const propertyByIdLookup = useSelector(getProperties);
  const rowData = useSelector(selectInterestPreviewAgGridData);
  const isLoading = useSelector(getIsFetchingInterestPreview);
  const isCreating = useSelector(getIsCreatingInterestCharges);
  const businessById = useSelector(getBusinesses) as Record<string, Business>;

  const { handleSubmit, isValid, setValues, values } =
    useFormikContext<InterestPreviewFormValues>();
  const { businessIds, chargeFromDate, chargeToDate, creditDebit } = values;

  const businessOptions = useMemo<BusinessOption[]>(
    () =>
      Object.values(businessById)
        .map(business => ({ value: business.id, label: business.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [businessById],
  );
  // Businesses are cleared while they refetch; remembering every option seen
  // keeps the selected chips showing what will still be submitted.
  const seenBusinessOptionById = useRef(new Map<string, BusinessOption>());
  const getSelectedBusinessOptions = useCallback(
    (value: string[] | null) => {
      if (!Array.isArray(value)) return [];
      businessOptions.forEach(option =>
        seenBusinessOptionById.current.set(option.value, option),
      );
      return value.flatMap(id => seenBusinessOptionById.current.get(id) ?? []);
    },
    [businessOptions],
  );

  const context = useMemo(() => ({ propertyByIdLookup }), [propertyByIdLookup]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    const updateGrandTotalRow = () =>
      event.api.setGridOption(
        "grandTotalRow",
        event.api.getValueColumns().length > 0 ? "bottom" : undefined,
      );
    updateGrandTotalRow();
    event.api.addEventListener("columnValueChanged", updateGrandTotalRow);
  }, []);

  useEffect(() => {
    if (!chargeToDate || !isValid) {
      dispatch(InterestPreviewAction.reset());
      return;
    }

    dispatch(
      InterestPreviewAction.request({
        chargeToDate,
        chargeFromDate: chargeFromDate || null,
        creditDebit,
        businessIds,
      }),
    );

    return () => {
      dispatch(InterestPreviewAction.reset());
    };
  }, [
    chargeFromDate,
    chargeToDate,
    creditDebit,
    businessIds,
    dispatch,
    isValid,
  ]);

  const updateDates = (
    nextChargeFromDate: string | null,
    nextChargeToDate: string | null,
  ) => {
    setValues({
      ...values,
      chargeFromDate: nextChargeFromDate,
      chargeToDate: nextChargeToDate ?? "",
    });
  };

  if (!canUseInterestPreview) return null;

  return (
    <Dialog
      fullScreen
      open
      onClose={onClose}
      PaperProps={{ component: "form", onSubmit: handleSubmit }}
    >
      <DialogTitle onClose={onClose}>Preview Interest Charges</DialogTitle>
      <DialogContent dividers>
        <Column fullWidth fullHeight gridGap={2}>
          <Grid container spacing={2}>
            <InputDateRange
              updateDates={updateDates}
              startDateFieldName="chargeFromDate"
              endDateFieldName="chargeToDate"
              includeTime={false}
              tooltip={DATE_RANGE_TOOLTIP}
              label="Date Range"
              startDateLabel="Charge From"
              endDateLabel="Charge To"
            />
            <Grid item xs={12}>
              <Autocomplete
                name="businessIds"
                label="Businesses"
                tooltip="Leave empty to include all businesses subject to interest for this period."
                placeholder={
                  businessIds.length ? undefined : BUSINESSES_PLACEHOLDER
                }
                options={businessOptions}
                getOptionValue={getBusinessOptionValue}
                getOptionSelected={(
                  option: BusinessOption,
                  value: BusinessOption,
                ) => option.value === value.value}
                getSelectedOption={getSelectedBusinessOptions}
                limitTags={MAX_VISIBLE_BUSINESS_TAGS}
                multiple
                disableCloseOnSelect
              />
            </Grid>
            <Grid item xs={12}>
              <OptionTogglerField
                name="creditDebit"
                label="Document Type"
                labelPosition="top"
                options={DOCUMENT_TYPE_OPTIONS}
              />
            </Grid>
          </Grid>

          {chargeToDate && (
            <AgGridTable
              rowData={rowData}
              columnDefs={columnDefs}
              loading={isLoading}
              context={context}
              tableName={AgGridTables.INTEREST_PREVIEW}
              onGridReady={onGridReady}
              hideHeader
              getRowId={getInterestPreviewRowId}
              pagination={false}
            />
          )}
        </Column>
      </DialogContent>
      <DialogActions>
        <SecondaryButton data-tour="close" type="button" onClick={onClose}>
          Close
        </SecondaryButton>
        <Button
          data-tour="create-interest-charges"
          type="submit"
          disabled={!isValid || isLoading || isCreating}
        >
          Create Interest Charges
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function InterestPreview(props: InterestPreviewProps) {
  const dispatch = useDispatch();
  const { businessIds = [] } =
    useSelector<RootState, InterestPreviewModalContext | null>(
      getContextByModalType(ModalTypes.InterestPreview),
    ) ?? {};
  const initialValues: InterestPreviewFormValues = {
    businessIds,
    chargeFromDate: null,
    chargeToDate: formatISO8601DateString(new Date()),
    creditDebit: CreditDebit.BOTH,
  };
  const onSubmit = ({
    businessIds: submittedBusinessIds,
    chargeFromDate,
    chargeToDate,
    creditDebit,
  }: InterestPreviewFormValues) => {
    dispatch(
      InterestPreviewAction.create({
        chargeToDate,
        chargeFromDate: chargeFromDate || null,
        creditDebit,
        businessIds: submittedBusinessIds,
      }),
    );
  };

  return (
    <Formik<InterestPreviewFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
    >
      <InterestPreviewModal {...props} />
    </Formik>
  );
}

export default InterestPreview;
